import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { DemoBanner, PlaceholderTag } from '../components/lumina/Placeholder';

function money(v: number): string {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/** Admin · Ranking & Leaderboard — nova (Fluxo 4 Fase 2). Dado real: parceiros ordenados por
 *  GMV (mesmo `GET /api/admin/partners`, sem endpoint de ranking dedicado — ordena no
 *  cliente). O protótipo tem "clubes" (Diamond/Ouro/Prata) e selos VIP que não existem no
 *  modelo da Storeo — ficam marcados "exemplo", só a posição e o GMV são reais. */
export function Ranking() {
  const partners = useQuery({ queryKey: ['admin', 'partners', 'ranking'], queryFn: () => api.partners({ size: 50 }) });
  const ranked = [...(partners.data ?? [])].filter((p) => p.storeId).sort((a, b) => b.gmv - a.gmv);
  const top3 = ranked.slice(0, 3);
  const rest = ranked.slice(3, 13);

  return (
    <>
      <div className="relative overflow-hidden rounded-2xl bg-surface-container-low p-space-xl shadow-xl">
        <div className="relative z-10 flex flex-col gap-space-2xs">
          <h1 className="font-headline-lg text-headline-lg text-on-surface font-bold">Ranking & Leaderboard</h1>
          <p className="font-body-md text-body-md text-on-surface-variant">Lojas ordenadas por GMV — dado real, sem clubes/selos (isso não existe na Storeo).</p>
        </div>
      </div>

      {partners.isLoading ? (
        <p className="font-body-sm text-body-sm text-on-surface-variant">Carregando…</p>
      ) : ranked.length === 0 ? (
        <p className="font-body-sm text-body-sm text-on-surface-variant">Nenhuma loja publicada ainda.</p>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-space-md">
            {top3.map((p, i) => (
              <div key={p.id} className={`rounded-2xl p-space-lg shadow-xl flex flex-col gap-space-sm ${i === 0 ? 'bg-linear-to-br from-primary-container/30 to-surface-container-low md:order-2' : 'bg-surface-container-low'} ${i === 1 ? 'md:order-1' : i === 2 ? 'md:order-3' : ''}`}>
                <div className="flex items-center gap-space-xs">
                  <span className="font-headline-lg text-headline-lg text-primary font-bold">#{i + 1}</span>
                  <PlaceholderTag label={i === 0 ? 'clube diamond (exemplo)' : 'clube ouro (exemplo)'} />
                </div>
                <Link to={`/parceiros/${p.id}`} className="font-headline-sm text-headline-sm text-on-surface font-bold hover:text-primary transition-colors">
                  {p.storeName}
                </Link>
                <span className="font-label-sm text-label-sm text-outline">{p.subdomain} · {p.name}</span>
                <div className="mt-space-xs">
                  <span className="font-label-sm text-label-sm text-outline uppercase tracking-wider">GMV</span>
                  <div className="font-headline-sm text-headline-sm text-secondary font-bold">{money(p.gmv)}</div>
                </div>
              </div>
            ))}
          </div>

          <section className="flex flex-col gap-space-md">
            <div className="flex items-center justify-between">
              <h2 className="font-headline-sm text-headline-sm text-on-surface font-bold">Classificação Geral (#4 em diante)</h2>
              <span className="font-label-sm text-label-sm text-outline">{ranked.length} lojas ranqueadas</span>
            </div>
            <div className="bg-surface-container-low rounded-2xl p-space-md shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="text-outline font-label-sm text-label-sm uppercase tracking-wider bg-surface-container-lowest/60">
                      <th className="py-space-sm px-space-md rounded-l-lg">Pos.</th>
                      <th className="py-space-sm px-space-sm">Loja</th>
                      <th className="py-space-sm px-space-md text-right rounded-r-lg">GMV</th>
                    </tr>
                  </thead>
                  <tbody className="text-body-sm font-body-sm">
                    {rest.map((p, i) => (
                      <tr key={p.id} className="hover:bg-surface-container/60 transition-colors">
                        <td className="py-space-md px-space-md font-mono text-outline">#{i + 4}</td>
                        <td className="py-space-md px-space-sm">
                          <Link to={`/parceiros/${p.id}`} className="text-on-surface font-semibold hover:text-primary transition-colors">
                            {p.storeName}
                          </Link>
                          <div className="text-outline font-label-sm text-label-sm">{p.subdomain}</div>
                        </td>
                        <td className="py-space-md px-space-md text-right font-mono text-secondary font-bold">{money(p.gmv)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        </>
      )}

      <DemoBanner>Clubes (Diamond/Ouro/Prata), selos VIP e "Hall da Fama" do protótipo não existem — só posição e GMV são reais.</DemoBanner>
    </>
  );
}
